import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';

function ReferencesFinder({ articleId, articleName }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [foundReferences, setFoundReferences] = useState([]); // Lista de referencias de cliente encontradas
    const [selectedReference, setSelectedReference] = useState(null); // Referencia seleccionada para ver detalles
    const [supplierDetails, setSupplierDetails] = useState([]); // Detalles de proveedores de la referencia seleccionada
    const [searched, setSearched] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    // Mock DB: Simulación de resultados paginados de referencias
    // Estructura: Referencia Cliente -> ID
    const mockReferencesDB = [
        { id: 1, clientRef: 'REF-FREI-001' },
        { id: 2, clientRef: 'REF-FREI-002' },
        { id: 3, clientRef: 'REF-FREI-003' },
        { id: 4, clientRef: 'REF-FREI-004' },
        { id: 5, clientRef: 'REF-FREI-005' },
    ];

    // Mock DB: Detalles de proveedores por referencia
    const mockSupplierDetailsDB = {
        'REF-FREI-001': [
            { id: 1, supplier: 'Pescanova', supplierRef: 'PES-AA-01' },
            { id: 2, supplier: 'Makro', supplierRef: 'MK-99-X' },
            { id: 3, supplier: 'Carrefour', supplierRef: 'CRF-2023' }
        ],
        'REF-FREI-002': [
            { id: 4, supplier: 'Mercadona', supplierRef: 'HACENDADO-55' }
        ],
        'REF-FREI-003': [
            { id: 5, supplier: 'Lidl', supplierRef: 'LIDL-DE-99' },
            { id: 6, supplier: 'Aldi', supplierRef: 'ALD-ES-11' }
        ]
    };

    const handleSearch = async (isNewSearch = true) => {
        if (!searchTerm.trim()) return;

        console.log(`Buscando referencias cliente: ${searchTerm}, Página: ${isNewSearch ? 1 : page + 1}`);
        
        // Simulación de llamada al backend
        // En un caso real, aquí se haría axios.post(...) con page y searchTerm
        
        let newResults = [];
        if (searchTerm.toLowerCase().includes('ref')) {
             newResults = mockReferencesDB.slice(0, 3); // Simular paginación de 3 en 3
             if (!isNewSearch && page === 1) {
                 newResults = mockReferencesDB.slice(3, 5); // Segunda página simulada
             }
        }

        if (isNewSearch) {
            setFoundReferences(newResults);
            setPage(1);
            setHasMore(true); // Simulado: asume que hay más si es la primera búsqueda exitosa
            setSelectedReference(null);
            setSupplierDetails([]);
        } else {
            setFoundReferences([...foundReferences, ...newResults]);
            setPage(page + 1);
            setHasMore(false); // Simulado: fin de resultados tras la segunda página
        }
        
        setSearched(true);
    };

    const handleLoadMore = () => {
        handleSearch(false);
    };

    const handleSelectReference = (ref) => {
        console.log(`Seleccionada referencia: ${ref.clientRef}`);
        setSelectedReference(ref);
        
        // Cargar detalles de proveedores para esta referencia
        const details = mockSupplierDetailsDB[ref.clientRef] || [];
        setSupplierDetails(details);
    };

    const handleClear = () => {
        setSearchTerm('');
        setFoundReferences([]);
        setSelectedReference(null);
        setSupplierDetails([]);
        setSearched(false);
        setPage(1);
        setHasMore(false);
    };

    return (
        <Container className="mt-3">
            <Form className="mb-4">
                <Row className="align-items-end">
                    <Col xs={12} md={8}>
                        <Form.Group controlId="searchClientRef" className="mb-3 mb-md-0">
                            <Form.Label>Buscar por Referencia Freiremar</Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Introduce referencia..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSearch(true);
                                    }
                                }}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={4} className="mt-2 mt-md-0">
                        <Button 
                            variant="outline-primary" 
                            type="button" 
                            className="me-2" 
                            onClick={() => handleSearch(true)}
                        >
                            <i className="fas fa-search"></i>
                        </Button>
                        <Button variant="outline-danger" onClick={handleClear} disabled={!searched}>
                            <i className="fas fa-trash-alt"></i>
                        </Button>
                    </Col>
                </Row>
            </Form>

            {searched && (
                <Row>
                    {/* Lista de Referencias Encontradas */}
                    <Col md={selectedReference ? 4 : 12} className="mb-3">
                        <h5 className="mb-3">Referencias Encontradas</h5>
                        {foundReferences.length > 0 ? (
                            <div className="list-group">
                                {foundReferences.map((ref) => (
                                    <button
                                        key={ref.id}
                                        type="button"
                                        className={`list-group-item list-group-item-action ${selectedReference?.id === ref.id ? 'active' : ''}`}
                                        onClick={() => handleSelectReference(ref)}
                                    >
                                        {ref.clientRef}
                                    </button>
                                ))}
                                {hasMore && (
                                    <button 
                                        type="button" 
                                        className="list-group-item list-group-item-action text-center text-primary"
                                        onClick={handleLoadMore}
                                    >
                                        <strong><i className="fas fa-plus-circle"></i> Ver más</strong>
                                    </button>
                                )}
                            </div>
                        ) : (
                            <Alert variant="warning">
                                No se encontraron referencias.
                            </Alert>
                        )}
                    </Col>

                    {/* Detalles de Proveedores para la Referencia Seleccionada */}
                    {selectedReference && (
                        <Col md={8}>
                            <h5 className="mb-3">
                                Proveedores para: <span className="text-primary">{selectedReference.clientRef}</span>
                            </h5>
                            {supplierDetails.length > 0 ? (
                                <Table striped bordered hover responsive size="sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Proveedor</th>
                                            <th>Referencia del Proveedor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {supplierDetails.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.supplier}</td>
                                                <td>{item.supplierRef}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <Alert variant="info">
                                    Esta referencia no tiene proveedores asociados.
                                </Alert>
                            )}
                        </Col>
                    )}
                </Row>
            )}
        </Container>
    );
}

export default ReferencesFinder;